import { apiGet } from './apiClient.js';

/**
 * The code a new record would get, for pre-filling the code field so it is
 * never empty. A suggestion only — nothing is reserved, the user may change it,
 * and the server re-checks uniqueness on save. `null` when it cannot be read
 * (no permission, offline): the form then simply leaves the field to the server.
 */
export async function fetchNextCode(base: string, entity: string): Promise<string | null> {
  try {
    const result = await apiGet<{ code?: string }>(
      `${base}/api/v1/master-data/next-code?entity=${encodeURIComponent(entity)}`,
    );
    return typeof result.code === 'string' && result.code !== '' ? result.code : null;
  } catch {
    return null;
  }
}

/** Chart-of-accounts suggestion for a parent (the parent fixes the account type and prefix). */
export interface AccountCodeSuggestion {
  readonly code: string;
  readonly parentRef: string | null;
  readonly levelSequence: number;
  readonly accountType: { readonly ref: string; readonly name: string } | null;
  readonly normalBalance: 'debit' | 'credit' | 'either' | null;
  readonly conventionDetected: boolean;
}

export async function fetchNextAccountCode(
  base: string,
  parentRef: string,
  levelSequence?: number,
): Promise<AccountCodeSuggestion | null> {
  const query = new URLSearchParams();
  if (parentRef) query.set('parentRef', parentRef);
  if (levelSequence !== undefined) query.set('levelSequence', String(levelSequence));
  try {
    return await apiGet<AccountCodeSuggestion>(
      `${base}/api/v1/finance/accounts/next-code?${query.toString()}`,
    );
  } catch {
    return null;
  }
}

/** The quote/order number that will probably be issued — shown read-only, never editable. */
export async function fetchNextDocumentRef(
  base: string,
  type: 'quote' | 'order',
): Promise<string | null> {
  try {
    const result = await apiGet<{ ref?: string | null }>(
      `${base}/api/v1/sales/next-ref?type=${type}`,
    );
    return result.ref ?? null;
  } catch {
    return null;
  }
}

/** Treasury / bank account code suggestion (finance permissions, separate from master data). */
export async function fetchNextFinanceCode(
  base: string,
  entity: 'treasury_account' | 'bank_account',
): Promise<string | null> {
  try {
    const result = await apiGet<{ code?: string }>(
      `${base}/api/v1/finance/treasury/next-code?entity=${entity}`,
    );
    return typeof result.code === 'string' && result.code !== '' ? result.code : null;
  } catch {
    return null;
  }
}
